/**
 * File Type Utilities
 * Centralized file type definitions and validation
 */

export const FILE_TYPES = {
  // Microsoft Office
  WORD: ['doc', 'docx'],
  EXCEL: ['xls', 'xlsx', 'csv'],
  POWERPOINT: ['ppt', 'pptx'],
  
  // Text Documents
  TEXT: ['txt', 'rtf', 'md'],
  
  // Media
  VIDEO: ['mp4'],
  IMAGE: ['jpg', 'jpeg', 'png'],
  
  // Technical
  PDF: ['pdf'],
  CAD: ['dwg', 'dxf'],
} as const;

export const FILE_CATEGORIES = {
  OFFICE: 'Office Documents',
  TEXT: 'Text Documents',
  MEDIA: 'Media',
  TECHNICAL: 'Technical Drawings',
  PDF: 'PDF Documents',
} as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

export const MIME_TYPES: Record<string, string> = {
  // Microsoft Office
  'doc': 'application/msword',
  'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'xls': 'application/vnd.ms-excel',
  'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'csv': 'text/csv',
  'ppt': 'application/vnd.ms-powerpoint',
  'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  
  // Text
  'txt': 'text/plain',
  'rtf': 'application/rtf',
  'md': 'text/markdown',
  
  // Media
  'mp4': 'video/mp4',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'png': 'image/png',
  
  // Technical
  'pdf': 'application/pdf',
  'dwg': 'image/vnd.dwg',
  'dxf': 'image/vnd.dxf',
};

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
}

/**
 * Get file category based on extension
 */
export function getFileCategory(extension: string): string {
  const ext = extension.toLowerCase();
  
  if (FILE_TYPES.WORD.includes(ext as any)) return FILE_CATEGORIES.OFFICE;
  if (FILE_TYPES.EXCEL.includes(ext as any)) return FILE_CATEGORIES.OFFICE;
  if (FILE_TYPES.POWERPOINT.includes(ext as any)) return FILE_CATEGORIES.OFFICE;
  if (FILE_TYPES.TEXT.includes(ext as any)) return FILE_CATEGORIES.TEXT;
  if (FILE_TYPES.VIDEO.includes(ext as any)) return FILE_CATEGORIES.MEDIA;
  if (FILE_TYPES.IMAGE.includes(ext as any)) return FILE_CATEGORIES.MEDIA;
  if (FILE_TYPES.PDF.includes(ext as any)) return FILE_CATEGORIES.PDF;
  if (FILE_TYPES.CAD.includes(ext as any)) return FILE_CATEGORIES.TECHNICAL;
  
  return 'Other';
}

/**
 * Check if file type is supported
 */
export function isSupportedFileType(filename: string): boolean {
  const ext = getFileExtension(filename);
  return Object.values(FILE_TYPES).some(types => types.includes(ext as any));
}

/**
 * Validate file size
 */
export function validateFileSize(size: number): { valid: boolean; error?: string } {
  if (size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
    };
  }
  return { valid: true };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get Material-UI icon name for file type
 */
export function getFileIcon(extension: string): string {
  const ext = extension.toLowerCase();
  
  if (FILE_TYPES.WORD.includes(ext as any)) return 'Description';
  if (FILE_TYPES.EXCEL.includes(ext as any)) return 'TableChart';
  if (FILE_TYPES.POWERPOINT.includes(ext as any)) return 'Slideshow';
  if (FILE_TYPES.TEXT.includes(ext as any)) return 'TextSnippet';
  if (FILE_TYPES.VIDEO.includes(ext as any)) return 'VideoFile';
  if (FILE_TYPES.IMAGE.includes(ext as any)) return 'Image';
  if (FILE_TYPES.PDF.includes(ext as any)) return 'PictureAsPdf';
  if (FILE_TYPES.CAD.includes(ext as any)) return 'Architecture';
  
  return 'InsertDriveFile';
}

/**
 * Get color for file type badge
 */
export function getFileTypeColor(extension: string): string {
  const ext = extension.toLowerCase();
  
  if (FILE_TYPES.WORD.includes(ext as any)) return '#2932E5'; // Primary
  if (FILE_TYPES.EXCEL.includes(ext as any)) return '#107C41'; // Excel green
  if (FILE_TYPES.POWERPOINT.includes(ext as any)) return '#D24726'; // PowerPoint red
  if (FILE_TYPES.TEXT.includes(ext as any)) return '#4A5565'; // Body text
  if (FILE_TYPES.VIDEO.includes(ext as any)) return '#E91E63'; // Pink
  if (FILE_TYPES.IMAGE.includes(ext as any)) return '#9C27B0'; // Purple
  if (FILE_TYPES.PDF.includes(ext as any)) return '#FF8555'; // Heading color
  if (FILE_TYPES.CAD.includes(ext as any)) return '#FF8555'; // Heading color
  
  return '#4A5565';
}

/**
 * Check if file can be previewed in browser
 */
export function canPreviewInBrowser(extension: string): boolean {
  const ext = extension.toLowerCase();
  return (
    FILE_TYPES.PDF.includes(ext as any) ||
    FILE_TYPES.IMAGE.includes(ext as any) ||
    FILE_TYPES.VIDEO.includes(ext as any) ||
    FILE_TYPES.TEXT.includes(ext as any)
  );
}

/**
 * Check if file needs external viewer (Office docs)
 */
export function needsExternalViewer(extension: string): boolean {
  const ext = extension.toLowerCase();
  return (
    FILE_TYPES.WORD.includes(ext as any) ||
    FILE_TYPES.EXCEL.includes(ext as any) ||
    FILE_TYPES.POWERPOINT.includes(ext as any)
  );
}

/**
 * Get accepted file types for input element
 */
export function getAcceptedFileTypes(): string {
  const allExtensions = Object.values(FILE_TYPES).flat();
  return allExtensions.map(ext => `.${ext}`).join(',');
}

/**
 * Get a user-friendly list of all supported file extensions
 */
export function getSupportedFileTypesList(): string {
  const allExtensions = Object.values(FILE_TYPES).flat();
  return allExtensions.map(ext => `.${ext}`).join(', ');
}

/**
 * Get a detailed description of supported file types
 */
export function getSupportedFileTypesDescription(): string {
  return 'Documents (.doc, .docx, .txt, .rtf, .md, .pdf), Spreadsheets (.xls, .xlsx, .csv), Presentations (.ppt, .pptx), Media (.mp4, .jpg, .jpeg, .png), CAD Files (.dwg, .dxf)';
}

/**
 * Map file extension to database file_type enum value
 * Database expects: 'image', 'pdf', or 'application'
 */
export function getFileTypeForDatabase(extension: string): string {
  const ext = extension.toLowerCase();
  
  // PDF files
  if (FILE_TYPES.PDF.includes(ext as any)) {
    return 'pdf';
  }
  
  // Image files
  if (FILE_TYPES.IMAGE.includes(ext as any)) {
    return 'image';
  }
  
  // All other files (documents, spreadsheets, presentations, videos, CAD, text files)
  // Default to 'application' for Office docs, videos, CAD files, etc.
  return 'application';
}
